import React, { useEffect, useState } from 'react';
import { MapPin, Users, FileText, Settings, Search, MoreHorizontal, Eye, Ban, Trash2 } from 'lucide-react';
import { api, handleApiError } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import Logo from './common/Logo';
import Toast from './common/Toast';

const WellsiteAdmin = () => {
  const [activeTab, setActiveTab] = useState('Users');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserItem | null>(null);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedWell, setSelectedWell] = useState<WellItem | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showWellDetailsModal, setShowWellDetailsModal] = useState(false);
  const [selectedWellDetails, setSelectedWellDetails] = useState<WellItem | null>(null);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [createPostForm, setCreatePostForm] = useState({
    type: 'Drilling',
    company: '',
    well: '',
    location: '',
    content: ''
  });
  const [selectedPost, setSelectedPost] = useState<Report | null>(null);
  const [showPostDetailsModal, setShowPostDetailsModal] = useState(false);
  const [showNotImplementedModal, setShowNotImplementedModal] = useState(false);
  const [pendingFeature, setPendingFeature] = useState('');
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' } | null>(null);

  // Simple helper to surface unimplemented features to the user
  // TODO: remove once the corresponding feature is available
  const openNotImplementedModal = (feature: string) => {
    setPendingFeature(feature);
    setShowNotImplementedModal(true);
  };

  interface UserItem {
    id: string;
    name: string;
    email: string;
    company?: string;
    status: string;
    joinDate: string;
    lastActive?: string;
    postsSubmitted: number;
    phone?: string;
    department?: string;
    role?: string;
    location?: string;
    isFin: boolean;
    activityLog: Array<{ id: string; action: string; details: string; timestamp: string }>;
    recentPosts: Array<{ id: string; type: string; location: string; status: string; date: string }>;
  }

  interface Report {
    id: string;
    user: string;
    userEmail: string;
    userCompany?: string;
    type: string;
    location?: string;
    coordinates?: string;
    wellName?: string;
    company?: string;
    well?: string;
    content: string;
    status: string;
    submittedAt: string;
    submittedDate: string;
    tags: string[];
    attachments: Array<{ name: string; size: string; type: string }>;
    aiAnalysis?: { riskLevel: string; keywords: string[]; sentiment: string; confidence: number };
    flagReason?: string;
  }

  interface WellItem {
    id: string;
    name: string;
    location: string;
    company: string;
    status: string;
    totalPosts: number;
    lastActivity: string;
    uwi?: string;
    country?: string;
    state?: string;
    county?: string;
    latitude?: number;
    longitude?: number;
    timeline: Array<{ id: string; user: string; type: string; content: string; timestamp: string; status: string }>;
  }

  interface CompanyItem {
    id: string;
    name: string;
    address: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone?: string;
    logo?: string;
  }

  interface Stats {
    totalUsers: number;
    activeUsers: number;
    onlineUsers: number;
    pendingReports: number;
    flaggedPosts: number;
    totalPosts: number;
  }
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showAdminProfile, setShowAdminProfile] = useState(false);
  const navigate = useNavigate();
  
  // Pagination helper function
  const paginate = (items: any[], currentPage: number, itemsPerPage: number) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };
  
  // Get total pages
  const getTotalPages = (totalItems: number, itemsPerPage: number) => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  // Reset to page 1 when changing tabs
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const [users, setUsers] = useState<UserItem[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [wells, setWells] = useState<WellItem[]>([]);
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    activeUsers: 0,
    onlineUsers: 0,
    pendingReports: 0,
    flaggedPosts: 0,
    totalPosts: 0,
  });

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } catch {
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      navigate('/login', { replace: true });
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.users.getAll({ limit: 100 });
      const fetched = (res.data?.data?.users || []).map((u: any) => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
        email: u.email,
        company: u.company || '',
        status: u.isActive ? 'active' : 'suspended',
        joinDate: new Date(u.createdAt).toLocaleDateString(),
        lastActive: u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'N/A',
        postsSubmitted: 0,
        phone: u.phone || '',
        department: '',
        role: u.role,
        location: '',
        isFin: u.isFin,
        activityLog: [],
        recentPosts: [],
      }));
      setUsers(fetched);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  };

  const fetchReports = async () => {
    try {
      const res = await api.activities.getAll({ limit: 100 });
      const fetched = (res.data?.data?.activities || []).map((r: any) => ({
        id: r.id,
        user: `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim(),
        userEmail: r.user?.email || '',
        userCompany: r.user?.company,
        type: r.type,
        location: r.location,
        coordinates: '',
        wellName: r.well?.name,
        company: r.user?.company,
        well: r.well?.name,
        content: r.description,
        status: 'approved',
        submittedAt: new Date(r.createdAt).toLocaleString(),
        submittedDate: new Date(r.createdAt).toLocaleString(),
        tags: [],
        attachments: [],
      }));
      setReports(fetched);
    } catch (err) {
      console.error('Failed to fetch reports', err);
    }
  };

  const fetchWells = async () => {
    try {
      const res = await api.wells.getAll({ limit: 100 });
      const fetched = (res.data?.data?.wells || []).map((w: any) => ({
        id: w.id,
        name: w.name,
        location: w.location,
        company: w.company || '',
        status: w.status.toLowerCase(),
        totalPosts: w._count?.activities || 0,
        lastActivity: w.updatedAt ? new Date(w.updatedAt).toLocaleDateString() : '',
        uwi: w.permit,
        country: '',
        state: '',
        county: '',
        latitude: w.latitude,
        longitude: w.longitude,
        timeline: [],
      }));
      setWells(fetched);
    } catch (err) {
      console.error('Failed to fetch wells', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.companies.getAll({ limit: 100 });
      const fetched = (res.data?.data?.companies || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        address: c.address,
        address2: c.address2,
        city: c.city,
        state: c.state,
        zip: c.zip,
        country: c.country,
        phone: c.phone,
        logo: c.logo,
      }));
      setCompanies(fetched);
    } catch (err) {
      console.error('Failed to fetch companies', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.admin.getDashboard();
      const dash = res.data?.data;
      setStats({
        totalUsers: dash?.users?.total || 0,
        activeUsers: dash?.users?.active || 0,
        onlineUsers: dash?.connections?.totalUsers || 0,
        pendingReports: 0,
        flaggedPosts: 0,
        totalPosts: dash?.activities?.last30Days || 0,
      });
    } catch (err) {
      console.error('Failed to fetch dashboard', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchReports();
    fetchWells();
    fetchCompanies();
    fetchStats();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'suspended': return 'text-red-400';
      case 'approved': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'flagged': return 'text-red-400';
      case 'drilling': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'suspended': return 'bg-red-500/20 text-red-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'flagged': return 'bg-red-500/20 text-red-400';
      case 'drilling': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const handleUserAction = async (action: string, user: UserItem) => {
    try {
      if (action === 'View') {
        setSelectedUser(user);
        setShowUserDetailsModal(true);
        return;
      }
      switch (action) {
        case 'Suspend':
          await api.users.updateStatus(user.id, false);
          setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: 'suspended' } : u));
          break;
        case 'Activate':
          await api.users.updateStatus(user.id, true);
          setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: 'active' } : u));
          break;
        case 'Delete':
          await api.users.delete(user.id);
          setUsers(prev => prev.filter(u => u.id !== user.id));
          break;
        case 'Send Message':
          await api.notifications.create({ title: 'Message from admin', message: 'Hello', type: 'INFO', recipientId: user.id });
          break;
        case 'Reset Password':
          await api.auth.requestPasswordReset({ email: user.email });
          break;
        default:
          openNotImplementedModal(`${action} action for ${user.name}`);
          // TODO: support additional user admin actions
      }
    } catch (error) {
      setToast({ message: handleApiError(error), type: 'error' });
    }
  };

  const handleCreatePostSubmit = async () => {
    if (!createPostForm.content.trim()) {
      setToast({ message: 'Please enter post content.', type: 'error' });
      return;
    }
    try {
      const { type, company, well, location, content } = createPostForm;
      const payload: any = {
        type: type.toUpperCase(),
        action: type,
        description: content,
      };
      if (location.trim()) {
        payload.location = location;
      }
      if (company.trim() || well.trim()) {
        const metadata: any = {};
        if (company.trim()) metadata.company = company;
        if (well.trim()) metadata.well = well;
        payload.metadata = metadata;
      }
      await api.activities.create(payload);
      setCreatePostForm({
        type: 'Drilling',
        company: '',
        well: '',
        location: '',
        content: ''
      });
      setShowCreatePostModal(false);
      await fetchReports();
      setToast({ message: 'Post created successfully!' });
    } catch (error) {
      setToast({ message: handleApiError(error), type: 'error' });
    }
  };

  const handleReportAction = async (action: string, report: Report) => {
    try {
      if (action === 'View') {
        setSelectedPost(report);
        setShowPostDetailsModal(true);
        return;
      }
      if (action === 'Delete') {
        if (!window.confirm('Are you sure you want to delete this post?')) return;
        await api.activities.delete(report.id);
        setReports(prev => prev.filter(r => r.id !== report.id));
        setToast({ message: 'Post deleted successfully!' });
      } else {
        await api.activities.update(report.id, { metadata: { status: action.toLowerCase() } });
        setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: action.toLowerCase() } : r));
      }
    } catch (error) {
      setToast({ message: handleApiError(error), type: 'error' });
    }
  };

  const handleWellAction = async (action: string, well: WellItem) => {
    try {
      if (action === 'View') {
        await openWellDetails(well);
        return;
      }
      if (action === 'Delete') {
        if (!window.confirm('Are you sure you want to delete this well?')) return;
        await api.wells.delete(well.id);
        setWells(prev => prev.filter(w => w.id !== well.id));
        setToast({ message: 'Well deleted successfully!' });
      } else {
        openNotImplementedModal(`${action} action for ${well.name}`);
      }
    } catch (error) {
      setToast({ message: handleApiError(error), type: 'error' });
    }
  };

  const openWellDetails = async (well: WellItem) => {
    try {
      const res = await api.activities.getTimeline(well.id, 30);
      const timeline = (res.data?.data || []).map((t: any) => ({
        id: t.id,
        user: `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.trim(),
        type: t.type,
        content: t.description || t.action,
        timestamp: new Date(t.createdAt).toLocaleString(),
        status: 'approved',
      }));
      setSelectedWellDetails({ ...well, timeline });
      setShowWellDetailsModal(true);
    } catch (error) {
      setToast({ message: handleApiError(error), type: 'error' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" onClick={(e) => {
      // Close admin profile dropdown when clicking outside
      if (showAdminProfile && !(e.target as HTMLElement).closest('.relative')) {
        setShowAdminProfile(false);
      }
    }}>
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Logo className="h-6 w-auto" />
              <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm font-medium">
                Admin
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowCreatePostModal(true)}
                className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                Create Post
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowAdminProfile(!showAdminProfile)}
                  className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm hover:bg-blue-600 transition-colors"
                >
                  A
                </button>
                
                {/* Admin Profile Dropdown */}
                {showAdminProfile && (
                  <div className="absolute right-0 top-10 w-80 bg-black border border-gray-700 rounded-xl shadow-xl z-50">
                    <div className="p-6">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                          A
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Admin User</h3>
                          <p className="text-gray-400">admin@wellsite.com</p>
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium mt-1 inline-block">
                            Super Admin
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-4 mb-6">
                        <div>
                          <p className="text-gray-400 text-sm">Last Login</p>
                          <p className="text-white">Today at 9:15 AM</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Session Duration</p>
                          <p className="text-white">2 hours 34 minutes</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            setShowAdminProfile(false);
                            openNotImplementedModal('Profile settings');
                            // TODO: implement admin profile settings
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors text-left"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>Profile</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            setShowAdminProfile(false);
                            openNotImplementedModal('Preferences');
                            // TODO: implement admin preferences
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors text-left"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Preferences</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            setShowAdminProfile(false);
                            openNotImplementedModal('Security settings');
                            // TODO: implement security settings
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-gray-800 rounded-lg transition-colors text-left"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span>Security</span>
                        </button>
                        
                          <div className="border-t border-gray-700 pt-2 mt-4">
                            <button
                              onClick={() => {
                                setShowAdminProfile(false);
                                handleLogout();
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-3 text-red-400 hover:bg-gray-800 rounded-lg transition-colors text-left"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              <span>Sign Out</span>
                            </button>
                          </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Users</p>
                  <p className="text-2xl font-bold text-white">{stats.totalUsers.toLocaleString()}</p>
                </div>
                <Users className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">MAU</p>
                  <p className="text-2xl font-bold text-white">{stats.activeUsers.toLocaleString()}</p>
                </div>
                <Users className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Online</p>
                  <p className="text-2xl font-bold text-white">{stats.onlineUsers}</p>
                </div>
                <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Posts</p>
                  <p className="text-2xl font-bold text-white">{stats.totalPosts.toLocaleString()}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Flagged</p>
                  <p className="text-2xl font-bold text-white">{stats.flaggedPosts}</p>
                </div>
                <FileText className="w-8 h-8 text-red-400" />
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Pending</p>
                  <p className="text-2xl font-bold text-white">{stats.pendingReports}</p>
                </div>
                <FileText className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-gray-800 mb-8">
          {['Users', 'Wells', 'Posts', 'Companies', 'Settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-6 py-4 text-sm font-medium relative transition-colors ${
                activeTab === tab
                  ? 'text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-white rounded-full"></div>
              )}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${activeTab.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white"
          />
        </div>

        {/* Content */}
        {activeTab === 'Users' && (
          <>
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">User</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Company</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Last Active</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Posts</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {paginate(users, currentPage, itemsPerPage).map((user) => (
                      <tr key={user.id} className="hover:bg-gray-800/50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-gray-400 text-sm">{user.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-300">{user.company}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(user.status)}`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-300">{user.lastActive}</td>
                        <td className="px-6 py-4 text-gray-300">{user.postsSubmitted}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleUserAction('View', user)}
                              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Users Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-400">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, users.length)} of {users.length} users
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => openNotImplementedModal('Import users')}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Import Users"
                    // TODO: implement user import
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openNotImplementedModal('Export users')}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Export Users"
                    // TODO: implement user export
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12M12 16.5V3" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: getTotalPages(users.length, itemsPerPage) }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      currentPage === page 
                        ? 'bg-white text-black' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages(users.length, itemsPerPage)))}
                  disabled={currentPage === getTotalPages(users.length, itemsPerPage)}
                  className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'Posts' && (
          <>
            <div className="space-y-4">
              {paginate(reports, currentPage, itemsPerPage).map((report) => (
                <div key={report.id} className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2 flex-wrap">
                        <span className="text-white font-medium">{report.user}</span>
                        <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-xs font-medium">
                          {report.type}
                        </span>
                        {report.company && (
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium">
                            {report.company}
                          </span>
                        )}
                        {report.well && (
                          <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-medium">
                            {report.well}
                          </span>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(report.status)}`}>
                          {report.status}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm mb-2">{report.location}</p>
                      <p className="text-gray-300 leading-relaxed">{report.content}</p>
                      <p className="text-gray-500 text-sm mt-2">{report.submittedAt}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleReportAction('View', report)}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 text-gray-400" />
                      </button>
                      <button
                        onClick={() => handleReportAction('Delete', report)}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Delete Post"
                      >
                        <Trash2 className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                  {report.status === 'pending' && (
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleReportAction('Approve', report)}
                        className="flex items-center space-x-2 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleReportAction('Reject', report)}
                        className="flex items-center space-x-2 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>Reject</span>
                      </button>
                    </div>
                  )}
                  {report.status === 'flagged' && (
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleReportAction('Dismiss', report)}
                        className="flex items-center space-x-2 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                        </svg>
                        <span>Dismiss</span>
                      </button>
                      <button
                        onClick={() => handleReportAction('Delete', report)}
                        className="flex items-center space-x-2 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Posts Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-400">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, reports.length)} of {reports.length} posts
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => openNotImplementedModal('Import posts')}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Import Posts"
                    // TODO: implement post import
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openNotImplementedModal('Export posts')}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Export Posts"
                    // TODO: implement post export
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12M12 16.5V3" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: getTotalPages(reports.length, itemsPerPage) }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      currentPage === page 
                        ? 'bg-white text-black' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages(reports.length, itemsPerPage)))}
                  disabled={currentPage === getTotalPages(reports.length, itemsPerPage)}
                  className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'Wells' && (
          <div className="space-y-6">
            {/* Wells Header */}
            <div></div>

            {/* Wells Overview */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Well Name</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Location</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Company</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Posts</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Last Activity</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {paginate(wells, currentPage, itemsPerPage).map((well) => (
                      <tr key={well.id} className="hover:bg-gray-800/50">
                        <td className="px-6 py-4">
                          <p className="text-white font-medium">{well.name}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-300">{well.location}</td>
                        <td className="px-6 py-4 text-gray-300">{well.company}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(well.status)}`}>
                            {well.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-300">{well.totalPosts}</td>
                        <td className="px-6 py-4 text-gray-300">{well.lastActivity}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleWellAction('View', well)}
                              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-gray-400" />
                            </button>
                            <button
                              onClick={() => handleWellAction('Delete', well)}
                              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                              title="Delete Well"
                            >
                              <Trash2 className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Wells Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-400">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, wells.length)} of {wells.length} wells
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Import Wells"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openNotImplementedModal('Export wells')}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Export Wells"
                    // TODO: implement well export
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12M12 16.5V3" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: getTotalPages(wells.length, itemsPerPage) }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      currentPage === page 
                        ? 'bg-white text-black' 
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, getTotalPages(wells.length, itemsPerPage)))}
                  disabled={currentPage === getTotalPages(wells.length, itemsPerPage)}
                  className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Settings' && (
          <div className="space-y-6">
            {/* Branding Settings */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-6">Branding</h3>
              <div className="space-y-6">
                {/* Logo Upload */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-white font-medium mb-3">Light Theme Logo</h4>
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-gray-500 transition-colors bg-white/5">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-black" />
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">Current light logo</p>
                      <button className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm">
                        Upload New Logo
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-white font-medium mb-3">Dark Theme Logo</h4>
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-gray-500 transition-colors bg-black/20">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-8 h-8 bg-gray-800 border border-gray-600 rounded-lg flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">Current dark logo</p>
                      <button className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm">
                        Upload New Logo
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Logo Guidelines */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  <h5 className="text-white font-medium mb-2">Logo Guidelines</h5>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Recommended size: 200x200px minimum</li>
                    <li>• Supported formats: PNG, SVG, JPG</li>
                    <li>• Maximum file size: 2MB</li>
                    <li>• Square or rectangular aspect ratios work best</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* General Settings */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-6">General</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Auto-approve reports</p>
                    <p className="text-gray-400 text-sm">Automatically approve reports from verified users</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-white"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Email notifications</p>
                    <p className="text-gray-400 text-sm">Send email alerts for flagged content</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-white"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">User registration</p>
                    <p className="text-gray-400 text-sm">Allow new user registrations</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-white"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Companies' && (
          <div className="space-y-6">
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Company</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Location</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Phone</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Logo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {paginate(companies, currentPage, itemsPerPage).map((company) => (
                      <tr key={company.id} className="hover:bg-gray-800/50">
                        <td className="px-6 py-4">
                          <p className="text-white font-medium">{company.name}</p>
                          <p className="text-gray-400 text-sm">
                            {company.address}
                            {company.address2 ? `, ${company.address2}` : ''}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-gray-300">
                          {`${company.city}, ${company.state}, ${company.country} ${company.zip}`}
                        </td>
                        <td className="px-6 py-4 text-gray-300">{company.phone || ''}</td>
                        <td className="px-6 py-4">
                          {company.logo && (
                            <img src={company.logo} alt={company.name} className="h-8" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Companies Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-400">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, companies.length)} of {companies.length} companies
              </div>
            </div>
          </div>
        )}

        {/* Create Post Modal */}
        {showCreatePostModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowCreatePostModal(false)}
            ></div>
            
            <div className="relative bg-black border border-gray-700 rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white">Create Post</h2>
                <button 
                  onClick={() => setShowCreatePostModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Activity Type
                  </label>
                  <select
                    value={createPostForm.type}
                    onChange={(e) => setCreatePostForm(prev => ({...prev, type: e.target.value}))}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-white"
                  >
                    <option value="Drilling">Drilling</option>
                    <option value="Production">Production</option>
                    <option value="Completions">Completions</option>
                    <option value="Well Intervention">Well Intervention</option>
                    <option value="Reclamation">Reclamation</option>
                    <option value="Regulatory">Regulatory</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Activity Description
                  </label>
                  <textarea
                    value={createPostForm.content}
                    onChange={(e) => setCreatePostForm(prev => ({...prev, content: e.target.value}))}
                    rows={6}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white resize-none"
                    placeholder="Describe the activity, observations, or updates..."
                  />
                  <div className="text-right text-xs text-gray-400 mt-1">
                    {createPostForm.content.length}/1000
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    value={createPostForm.company}
                    onChange={(e) => setCreatePostForm(prev => ({...prev, company: e.target.value}))}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white"
                    placeholder="e.g., ExxonMobil"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Well (Optional)
                  </label>
                  <input
                    type="text"
                    value={createPostForm.well}
                    onChange={(e) => setCreatePostForm(prev => ({...prev, well: e.target.value}))}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white"
                    placeholder="e.g., Well #42H-15"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Location (Optional)
                  </label>
                  <input
                    type="text"
                    value={createPostForm.location}
                    onChange={(e) => setCreatePostForm(prev => ({...prev, location: e.target.value}))}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white"
                    placeholder="e.g., Ward County, TX"
                  />
                </div>

                <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-blue-200 text-sm">
                        This post will be automatically approved and visible to all users. 
                        Make sure the content follows community guidelines.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowCreatePostModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePostSubmit}
                    disabled={!createPostForm.content.trim()}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium ${
                      createPostForm.content.trim()
                        ? 'bg-white text-black hover:bg-gray-200'
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Create Post
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Well Details Modal */}
        {showWellDetailsModal && selectedWellDetails && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowWellDetailsModal(false)}
            ></div>
            
            <div className="relative bg-black border border-gray-700 rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedWellDetails.name}</h2>
                  <p className="text-gray-400">{selectedWellDetails.uwi}</p>
                </div>
                <button 
                  onClick={() => setShowWellDetailsModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Core Information */}
                  <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-medium text-white mb-4">Well Information</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-gray-400 text-sm">Well Name</p>
                        <p className="text-white font-medium">{selectedWellDetails.name}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">UWI (Unique Well Identifier)</p>
                        <p className="text-white font-mono">{selectedWellDetails.uwi}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Company</p>
                        <p className="text-white">{selectedWellDetails.company}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Status</p>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(selectedWellDetails.status)}`}>
                          {selectedWellDetails.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-medium text-white mb-4">Location</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-gray-400 text-sm">Country</p>
                        <p className="text-white">{selectedWellDetails.country}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">State/Province</p>
                        <p className="text-white">{selectedWellDetails.state}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">County/Parish</p>
                        <p className="text-white">{selectedWellDetails.county}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm">Latitude</p>
                          <p className="text-white font-mono">{selectedWellDetails.latitude}°</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Longitude</p>
                          <p className="text-white font-mono">{selectedWellDetails.longitude}°</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Activity Summary */}
                  <div className="md:col-span-2 bg-gray-900 border border-gray-700 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-white">Activity Summary</h3>
                      <button
                        onClick={() => {
                          setShowWellDetailsModal(false);
                          setSelectedWell(selectedWellDetails);
                        }}
                        className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      >
                        View Full Timeline
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-sm">Posts</p>
                        <p className="text-white font-medium">{selectedWellDetails.totalPosts}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Last Activity</p>
                        <p className="text-white">{selectedWellDetails.lastActivity}</p>
                      </div>
                    </div>
                    
                    {/* Recent Activity Preview */}
                    <div className="space-y-3">
                      <h4 className="text-white font-medium">Recent Activity</h4>
                      {selectedWellDetails.timeline.slice(0, 2).map((activity) => (
                        <div key={activity.id} className="bg-gray-800 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white text-sm font-medium">{activity.user}</span>
                            <span className="text-gray-400 text-xs">{activity.timestamp}</span>
                          </div>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                              {activity.type}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${getStatusBg(activity.status)}`}>
                              {activity.status}
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm line-clamp-2">{activity.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3 mt-6 pt-6 border-t border-gray-700">
                  <button
                    onClick={() => {
                      const coords = `${selectedWellDetails.latitude},${selectedWellDetails.longitude}`;
                      window.open(`https://www.google.com/maps/search/?api=1&query=${coords}`, '_blank');
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    View on Map
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${selectedWellDetails.name} (${selectedWellDetails.uwi}) - ${selectedWellDetails.latitude}, ${selectedWellDetails.longitude}`);
                      alert('Well details copied to clipboard!');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Copy Details
                  </button>
                  <button
                    onClick={() => openNotImplementedModal('Export well data')}
                    className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    // TODO: implement well data export
                  >
                    Export Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetailsModal && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowUserDetailsModal(false)}
            ></div>
            
            <div className="relative bg-black border border-gray-700 rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {selectedUser.name.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedUser.name}</h2>
                    <p className="text-gray-400">{selectedUser.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(selectedUser.status)}`}>
                        {selectedUser.status}
                      </span>
                      <span className="text-gray-500 text-sm">Joined {selectedUser.joinDate}</span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setShowUserDetailsModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* User Information */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 mb-6">
                      <h3 className="text-lg font-medium text-white mb-4">User Information</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-gray-400 text-sm">Company</p>
                          <p className="text-white">{selectedUser.company}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Department</p>
                          <p className="text-white">{selectedUser.department}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Role</p>
                          <p className="text-white">{selectedUser.role}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Location</p>
                          <p className="text-white">{selectedUser.location}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Phone</p>
                          <p className="text-white">{selectedUser.phone}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Last Active</p>
                          <p className="text-white">{selectedUser.lastActive}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedUser.isFin}
                            onChange={async (e) => {
                              const value = e.target.checked;
                              try {
                                await api.users.update(selectedUser.id, { isFin: value });
                                setSelectedUser(prev => prev ? { ...prev, isFin: value } : prev);
                                setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, isFin: value } : u));
                                setToast({ message: 'FIN updated successfully!' });
                              } catch (error) {
                                setToast({ message: handleApiError(error), type: 'error' });
                              }
                            }}
                            className="h-4 w-4"
                          />
                          <span className="text-gray-300 text-sm">FIN</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                      <h3 className="text-lg font-medium text-white mb-4">Statistics</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Posts Submitted</span>
                          <span className="text-white font-medium">{selectedUser.postsSubmitted}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Account Age</span>
                          <span className="text-white font-medium">2 months</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Approval Rate</span>
                          <span className="text-green-400 font-medium">94%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Activity & Reports */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Activity Log */}
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                      <h3 className="text-lg font-medium text-white mb-4">Activity Log</h3>
                      <div className="space-y-4">
                        {selectedUser.activityLog.map((activity, index) => (
                          <div key={activity.id} className="relative">
                            {/* Timeline line */}
                            {index < selectedUser.activityLog.length - 1 && (
                              <div className="absolute left-3 top-8 w-0.5 h-8 bg-gray-700"></div>
                            )}
                            
                            {/* Timeline dot */}
                            <div className="absolute left-1.5 top-2 w-3 h-3 bg-white rounded-full border-2 border-black"></div>
                            
                            {/* Activity content */}
                            <div className="ml-8">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="text-white font-medium">{activity.action}</p>
                                  <p className="text-gray-400 text-sm">{activity.details}</p>
                                </div>
                                <span className="text-gray-500 text-sm">{activity.timestamp}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Admin Actions */}
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                      <h3 className="text-lg font-medium text-white mb-4">Admin Actions</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => handleUserAction('Send Message', selectedUser)}
                          className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                        >
                          Send Message
                        </button>
                        <button
                          onClick={() => handleUserAction(selectedUser.status === 'active' ? 'Suspend' : 'Activate', selectedUser)}
                          className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                        >
                          {selectedUser.status === 'active' ? 'Suspend User' : 'Activate User'}
                        </button>
                        <button
                          onClick={() => handleUserAction('Edit Profile', selectedUser)}
                          className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                        >
                          Edit Profile
                        </button>
                        <button
                          onClick={() => handleUserAction('View Posts', selectedUser)}
                          className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                        >
                          All Posts
                        </button>
                        <button
                          onClick={() => handleUserAction('Reset Password', selectedUser)}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm font-medium"
                        >
                          Reset Password
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete ${selectedUser.name}? This action cannot be undone.`)) {
                              handleUserAction('Delete', selectedUser);
                              setShowUserDetailsModal(false);
                            }
                          }}
                          className="px-4 py-2 bg-gray-900 text-white border border-gray-600 rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                        >
                          Delete User
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showNotImplementedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowNotImplementedModal(false)}
            ></div>
            <div className="relative bg-black border border-gray-700 rounded-2xl w-full max-w-md mx-4">
              <div className="p-6 text-center">
                <h2 className="text-xl font-bold text-white mb-4">Feature Coming Soon</h2>
                <p className="text-gray-300 mb-6">{pendingFeature} is not yet available.</p>
                <button
                  onClick={() => setShowNotImplementedModal(false)}
                  className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

export default WellsiteAdmin;
